// Branding extension point: the per-customer identity that is baked into the desktop
// build at packaging time (product name, app id, icons, deeplink scheme), plus the
// auto-update switch and the Windows installer target.
//
// STRICT ON PURPOSE. Unlike the /api/me wire contracts (which use `.catchall` so an
// additive server change does not break older clients), every extension-point schema
// rejects unknown keys: a typo in a customer repo must fail `validate` loudly instead
// of being silently dropped.
//
// Only JSON-Schema-representable types are used here (no `z.date()`, no transforms),
// because the published JSON Schema is generated from these schemas with
// `z.toJSONSchema()`.
import { z } from 'zod';
/** Icon set, as paths relative to the customer repo root. */
export const BrandingIconsSchema = z.strictObject({
    /** macOS bundle icon. */
    icns: z.string().min(1),
    /** Windows installer + exe icon. */
    ico: z.string().min(1),
    /** Linux and in-app icon. */
    png: z.string().min(1),
});
const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'expected a hex colour (#rrggbb)');
/** The design-system steps of one palette, in the Gránit scale. The step names a role, not a lightness. */
export const PALETTE_STEPS = [
    '30',
    '50',
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900',
];
/**
 * One palette: every step as a hex colour. In a `light` palette the steps get darker (30 is the page
 * background, 900 the strongest text); in a `dark` palette they get lighter, so the same step keeps
 * its role in both modes. The direction is checked on `BrandingColorsSchema`.
 */
export const PaletteSchema = z.strictObject(Object.fromEntries(PALETTE_STEPS.map((step) => [step, HexColorSchema])));
/**
 * `neutral`: backgrounds, cards, text, borders, inputs. `primary`: header, navigation, headings,
 * secondary buttons, the selected item, the highlighted part of the name. `accent`: the main action button.
 */
export const ThemePalettesSchema = z.strictObject({
    neutral: PaletteSchema,
    primary: PaletteSchema,
    accent: PaletteSchema,
});
/** WCAG relative luminance of a `#rrggbb` colour, 0 (black) … 1 (white). */
export function relativeLuminance(hex) {
    const channel = (offset) => {
        const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
        return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}
function directionIssues(mode, palettes) {
    return ['neutral', 'primary', 'accent'].flatMap((name) => PALETTE_STEPS.slice(1).flatMap((step, index) => {
        const previous = PALETTE_STEPS[index];
        const delta = relativeLuminance(palettes[name][step]) - relativeLuminance(palettes[name][previous]);
        const wrong = mode === 'light' ? delta > 0 : delta < 0;
        const word = mode === 'light' ? 'lighter' : 'darker';
        return wrong ? [`colors.${mode}.${name}: step ${step} is ${word} than step ${previous}`] : [];
    }));
}
/** `light` is required; without `dark` the admin UIs run light-only and hide the mode toggle. */
export const BrandingColorsSchema = z
    .strictObject({ light: ThemePalettesSchema, dark: ThemePalettesSchema.optional() })
    .superRefine((colors, context) => {
    const messages = [
        ...directionIssues('light', colors.light),
        ...(colors.dark ? directionIssues('dark', colors.dark) : []),
    ];
    for (const message of messages)
        context.addIssue({ code: 'custom', message });
});
/**
 * Customer-facing copy overrides, per locale. The product ships `en` + `hu`; a key
 * absent here keeps the product default — including a whole absent locale, so overriding
 * only the Hungarian copy needs no empty `en: {}` counterpart.
 */
export const BrandingStringsSchema = z.strictObject({
    en: z.record(z.string(), z.string()).optional(),
    hu: z.record(z.string(), z.string()).optional(),
});
/**
 * Auto-update feed. `enabled: false` short-circuits the updater entirely — that is the
 * operations-managed rollout path (MSI + central deployment tooling), where the bank's
 * operators push new versions instead of the client pulling them.
 */
export const AutoUpdateSchema = z.strictObject({
    enabled: z.boolean(),
    /**
     * electron-updater `generic` feed URL. Required in practice when `enabled` is true.
     * http/https only — a bare `host:port` string would otherwise pass as a valid URL.
     */
    feedUrl: z.url({ protocol: /^https?$/ }).optional(),
});
/** Windows installer target. `nsis` is per-user with auto-update; `msi` is per-machine. */
export const WinTargetSchema = z.enum(['nsis', 'msi']);
/**
 * MSI-specific options. `upgradeCode` MUST stay stable across product versions —
 * changing it breaks the silent upgrade path for every already-installed machine.
 */
export const MsiOptionsSchema = z.strictObject({
    perMachine: z.boolean(),
    upgradeCode: z.uuid(),
});
/**
 * Who signs the installer: we (vendor) or the customer's own signing service. `certRef`
 * names a secret/certificate reference — never the certificate or its password.
 */
export const CodeSigningSchema = z.strictObject({
    provider: z.enum(['vendor', 'customer']),
    certRef: z.string().min(1).optional(),
});
export const BrandingSchema = z.strictObject({
    /** Product name shown in the installer, the window title and the OS app list. */
    appName: z.string().min(1),
    /** Reverse-DNS application identifier (electron-builder `appId`). */
    appId: z.string().min(1),
    /**
     * Deeplink scheme the installed app registers for and accepts (`<scheme>://…`). Defaults to
     * the product's own `echowork` (EW-143); a distribution that runs next to another EchoWork
     * install on one machine MUST set its own (recommended shape `<customer>-<product>`), or the
     * OS hands its links to whichever install it likes. The legacy `openwork` is retired: the
     * fork produces no such link (the auth callback is loopback), so nothing depends on it.
     */
    protocolScheme: z.string().min(1).default('echowork'),
    icons: BrandingIconsSchema,
    colors: BrandingColorsSchema.optional(),
    strings: BrandingStringsSchema.optional(),
    autoUpdate: AutoUpdateSchema,
    winTarget: WinTargetSchema.default('nsis'),
    msi: MsiOptionsSchema.optional(),
    codeSigning: CodeSigningSchema,
});
//# sourceMappingURL=branding.js.map